/**
 * FeelingStep component - Step 2 of the 4-step check-in flow.
 * Captures mood, energy level, body symptoms, and custom symptom text.
 */

import React from 'react';
import { MoodOption } from '../../types';

export interface FeelingStepData {
  energy_level: 'low' | 'medium' | 'high';
  mood?: MoodOption;
  body_symptoms: string[];
  custom_symptom?: string;
}

interface FeelingStepProps {
  data: FeelingStepData;
  onChange: (data: FeelingStepData) => void;
}

const ENERGY_LEVEL_OPTIONS: { value: 'low' | 'medium' | 'high'; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'from-red-400 to-red-500' },
  { value: 'medium', label: 'Medium', color: 'from-yellow-400 to-yellow-500' },
  { value: 'high', label: 'High', color: 'from-green-400 to-green-500' },
];

const MOOD_OPTIONS: { value: MoodOption; label: string }[] = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'irritated_stressed', label: 'Irritated / Stressed' },
  { value: 'low', label: 'Low' },
];

const BODY_SYMPTOMS: { value: string; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'breast_tenderness', label: 'Breast tenderness' },
  { value: 'bloated', label: 'Bloated' },
  { value: 'headache', label: 'Headache' },
  { value: 'cramps', label: 'Cramps' },
  { value: 'lower_back_ache', label: 'Lower back ache' },
  { value: 'muscle_soreness', label: 'Muscle soreness' },
  { value: 'feeling_heavy', label: 'Feeling heavy' },
  { value: 'poor_digestion', label: 'Poor digestion' },
  { value: 'others', label: 'Others' },
];

const FeelingStep: React.FC<FeelingStepProps> = ({ data, onChange }) => {
  const update = (partial: Partial<FeelingStepData>) => {
    onChange({ ...data, ...partial });
  };

  const handleSymptomToggle = (symptom: string) => {
    if (symptom === 'none') {
      update({ body_symptoms: ['none'], custom_symptom: undefined });
    } else {
      const current = data.body_symptoms.filter(s => s !== 'none');
      const newSymptoms = current.includes(symptom)
        ? current.filter(s => s !== symptom)
        : [...current, symptom];

      const finalSymptoms = newSymptoms.length === 0 ? ['none'] : newSymptoms;
      const clearCustom = !finalSymptoms.includes('others') ? { custom_symptom: undefined } : {};
      update({ body_symptoms: finalSymptoms, ...clearCustom });
    }
  };

  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
        How Are You Feeling?
      </h2>
      <p className="text-neutral-600 mb-6">
        Your energy, mood, and any symptoms today
      </p>

      {/* Energy Level */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Energy level
        </label>
        <div className="grid grid-cols-3 gap-3">
          {ENERGY_LEVEL_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ energy_level: option.value })}
              className={`p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                data.energy_level === option.value
                  ? `border-transparent bg-gradient-to-br ${option.color} text-white shadow-md`
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <div className={`text-sm font-bold ${data.energy_level === option.value ? 'text-white' : 'text-neutral-800'}`}>
                {option.label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mood */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Mood <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {MOOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ mood: data.mood === option.value ? undefined : option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                data.mood === option.value
                  ? 'border-accent-600 bg-accent-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Body Symptoms */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Body symptoms <span className="text-neutral-400 font-normal">(select all that apply)</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {BODY_SYMPTOMS.map((symptom) => (
            <button
              key={symptom.value}
              type="button"
              onClick={() => handleSymptomToggle(symptom.value)}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                data.body_symptoms.includes(symptom.value)
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className={`text-sm font-medium ${
                data.body_symptoms.includes(symptom.value) ? 'text-primary-800' : 'text-neutral-700'
              }`}>
                {symptom.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Symptom Input */}
      {data.body_symptoms.includes('others') && (
        <div>
          <label htmlFor="custom-symptom" className="block text-sm font-medium text-neutral-700 mb-2">
            Describe your symptom
          </label>
          <input
            id="custom-symptom"
            type="text"
            value={data.custom_symptom || ''}
            onChange={(e) => update({ custom_symptom: e.target.value || undefined })}
            className="input w-full"
            placeholder="e.g. dizziness, tingling..."
            maxLength={100}
            required
          />
          <p className="text-xs text-neutral-500 mt-1">
            {(data.custom_symptom || '').length} / 100 characters
          </p>
        </div>
      )}
    </div>
  );
};

export default FeelingStep;
